import { useResidentList } from '@/hooks/useResidentList';
import { ResidentListStatus } from '@/components/residentListStatus';
import { normalizeResidentSortType } from '@/utils/residentSort';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import dayjs from 'dayjs';
import { IResidentDFreeGraph } from '@/types/residents/dfreeGraph';
import { ResidentListHeader } from '@/components/residentListHeader';
import { DfreeGraphListItem } from './dfreeGraphListItem';
import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { residentListSettingsSelector } from '@/redux/config/residentListSettings';
import { RecordDialog } from '@/components/careRecord/recordDialog';
import { RecordModal } from '@/components/careRecord/recordModal';
import { navSelector, navSlice } from '@/redux/config/leftnav';
import { selfDataSelector } from '@/redux/config/selfData';
import { BatteryAlertModal } from '@/components/batteryAlertModal';
import { useFetchDFreeGraphList } from '@/hooks/multi/useFetchDFreeGraphList';
import { RawGraphCheckbox } from './rawGraphCheckbox';
import { DataAnomalyHintButton } from '../dataAnomalyHintButton';

const Container = css`
  padding: 0 16px;
`;

const createGraphDateRange = () => {
  return {
    start: formatUtcFetchTimestamp(dayjs().subtract(5, 'hour')),
    end: formatUtcFetchTimestamp(dayjs()),
  };
};

export const ProDfreeGraphList: React.FC = () => {
  const dispatch = useDispatch();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { badge } = useSelector(navSelector);
  const residentListSettings = useSelector(residentListSettingsSelector);
  const self = useSelector(selfDataSelector);
  const [resident, setResident] = React.useState<IResidentDFreeGraph>();
  const effectiveSort = normalizeResidentSortType(residentListSettings.sortType, false);
  const permittedUnitId = self.selfUser.hasUnitPerm ? self.selfUser.nursingHomeUnitId : null;
  const requestScope = JSON.stringify([self.selfUser.id, nursingHomeId, permittedUnitId, self.selfUser.serviceType]);
  const { fetchDFreeGrapListData } = useFetchDFreeGraphList();
  const [batteryAlertResident, setBatteryAlertResident] = React.useState<IResidentDFreeGraph | null>(null);
  const [gasBtteryAlertResident, setGasBtteryAlertResident] = React.useState<IResidentDFreeGraph | null>(null);
  const [isRawGraphVisible, setRawGraphVisible] = React.useState(false);
  const { t, i18n } = useTranslation();
  const isJa = i18n.language.startsWith('ja');

  const { data, residents: sortedResidents, status: fetchStatus, refresh: fetchResidents } = useResidentList<
    IResidentDFreeGraph, { residents: IResidentDFreeGraph[]; dateRange: { start: string; end: string } }
  >({
    scope: requestScope,
    enabled: !!self.selfUser.id,
    categories: residentListSettings.categories,
    sortType: effectiveSort,
    permittedUnitId,
    fetchData: async () => {
      const dateRange = createGraphDateRange();
      const residents = await fetchDFreeGrapListData(dateRange.start, dateRange.end);
      return { residents, dateRange };
    },
    onLoaded: (residents) => {
      setBatteryAlertResident(residents.find((v) => v.batteryAlertNotification) ?? null);
      setGasBtteryAlertResident(residents.find((v) => v.gasBatteryAlertNotification) ?? null);
    },
  });

  const UpdateBatteryAlert = async (residentId?: number) => {
    setBatteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredBatteryAlert: false,
      });
      fetchResidents();
    }
  };

  const UpdateGasBatteryAlert = async (residentId?: number) => {
    setGasBtteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredGasBatteryAlert: false,
      });
      fetchResidents();
    }
  };

  const handleOpenRecordModal = React.useCallback((resident: IResidentDFreeGraph) => {
    setResident(resident);
  }, []);

  const handleCloseRecordModal = React.useCallback(() => {
    setResident(undefined);
  }, []);

  const handleUpdate = React.useCallback(() => {
    fetchResidents();
  }, [fetchResidents]);

  // 通知アラートの消去とバッヂカウントの更新処理
  const handleResetNotification = React.useCallback(
    async (residentId: number, coverTypes?: string[]) => {
      let params = { residentId } as {
        residentId: number;
        uncaredBigBladder?: boolean;
        uncaredUrination?: boolean;
        uncaredGasPooped?: boolean;
        uncaredGasPrediction?: boolean;
        uncaredGotUp?: boolean;
        uncaredSensorError?: boolean;
        uncaredUrineReset?: boolean;
        uncaredZeroReset?: boolean;
        uncaredUpsideDown?: boolean;
        uncaredMoveUpperAdvice?: boolean;
        uncaredMoveLowerAdvice?: boolean;
      };

      const coverTypeMap: Record<string, keyof typeof params> = {
        bigBladder: 'uncaredBigBladder',
        urination: 'uncaredUrination',
        gasPooped: 'uncaredGasPooped',
        gasPrediction: 'uncaredGasPrediction',
        gotUp: 'uncaredGotUp',
        sensorError: 'uncaredSensorError',
        urineReset: 'uncaredUrineReset',
        zeroReset: 'uncaredZeroReset',
        upsideDown: 'uncaredUpsideDown',
        moveUpperAdvice: 'uncaredMoveUpperAdvice',
        moveLowerAdvice: 'uncaredMoveLowerAdvice',
      };

      const selectedCoverTypes = coverTypes ?? Object.keys(coverTypeMap);
      params = selectedCoverTypes.reduce((acc, type) => {
        const paramKey = coverTypeMap[type] as keyof typeof params;
        return paramKey ? { ...acc, [paramKey]: false } : acc;
      }, params);

      await api.put('/residents/:residentId/notifications/covers', params);
      if (badge.resident > 0) {
        dispatch(navSlice.actions.setResidentBadge(-1));
      }
      fetchResidents();
    },
    [fetchResidents, badge, dispatch],
  );

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('dfreeGraph', 'DFreeグラフ')}
        categories={residentListSettings.categories}
        sortType={residentListSettings.sortType}
        rightOfUpdateElement={isJa ? <DataAnomalyHintButton /> : null}
        onUpdate={handleUpdate}
        rightElement={
          <RawGraphCheckbox
            checked={isRawGraphVisible}
            onChange={(checked) => {
              setRawGraphVisible(checked);
            }}
          />
        }
      />
      {data && sortedResidents.length > 0 ? (
        sortedResidents.map((resident) => (
          <DfreeGraphListItem
            key={resident.id}
            resident={resident}
            fetch={handleUpdate}
            dateRange={data.dateRange}
            openDialog={handleOpenRecordModal}
            resetNotification={handleResetNotification}
            showRawGraph={isRawGraphVisible}
          />
        ))
      ) : (
        <ResidentListStatus status={fetchStatus} />
      )}
      {resident && (
        <RecordDialog isOpen={!!resident} dialogWidth={'640px'} onClose={handleCloseRecordModal}>
          <RecordModal
            resident={resident}
            resetNotification={handleResetNotification}
            onClose={handleCloseRecordModal}
            onUpdate={handleUpdate}
          />
        </RecordDialog>
      )}
      <BatteryAlertModal
        isOpen={!!batteryAlertResident}
        name={batteryAlertResident?.name}
        title={
          <div>
            {t('anAbnormalityHasBeenDetectedInTheDFreeDevice', 'DFree機器本体の異常を検知しました。')}
            <br />
            {t('pleaseStopUsingItAndContactCustomerSupport', '使用を中止し、サポート窓口にお問い合わせください。')}
          </div>
        }
        onClose={() => {
          UpdateBatteryAlert(batteryAlertResident?.id);
        }}
      >
        <div>
          {t('833-337-3387(833-DFREEUS)', '【電話番号】03-5459-1295')}
          <br />
          {t('mon-Fri9AM-5PMPST', '【受付時間】平日10:00~17:00')}
          <br />（{t('excludingWeekendAndHolidays', '土日祝日・年末年始を除く')}）
        </div>
      </BatteryAlertModal>
      <BatteryAlertModal
        isOpen={!!gasBtteryAlertResident}
        name={gasBtteryAlertResident?.name}
        title={
          <div>
            {t('anAbnormalityHasBeenDetectedInDFreeBenDevice', 'DFreeBenの異常を検知しました。')}
            <br />
            {t('pleaseStopUsingItAndContactCustomerSupport', '使用を中止し、サポート窓口にお問い合わせください。')}
          </div>
        }
        onClose={() => {
          UpdateGasBatteryAlert(gasBtteryAlertResident?.id);
        }}
      >
        <div>
          {t('833-337-3387(833-DFREEUS)', '【電話番号】03-5459-1295')}
          <br />
          {t('mon-Fri9AM-5PMPST', '【受付時間】平日10:00~17:00')}
          <br />（{t('excludingWeekendAndHolidays', '土日祝日・年末年始を除く')}）
        </div>
      </BatteryAlertModal>
    </div>
  );
};
